import {inject, Injectable} from '@angular/core';
import { DataService } from '@serv/data.service';
import { UtilsVarsComponentLlm } from '@llm/service/utils_main_comp_variables.service';
import { SpeechSynthesizerService, SpeechSettings } from '@llm/service/speech_synthesizer.service';
import {PlayMessageService} from '@llm/service/play_message.service';
import {Observable} from "rxjs";
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service"; // Importa o novo serviço de fala

/**
 * Serviço que administra uma coleção de mensagens e garante que cada uma se utilize
 * uma única vez por ciclo. Uma vez que se tenham utilizado todas as mensagens, o serviço
 * reinicia o ciclo para continuar o processo sem interrupções.
 *
 * Este serviço é útil em situações onde se requer uma rotação equitativa de mensagens,
 * eliminando a possibilidade de repetições até que todos tenham sido mostrados.
 */
@Injectable({
  providedIn: 'root'
})
export class MessageService { // Renomeado de MensajesService para MessageService
  private _user_data_service=inject(DataService) // Mantido para personalização
  private _utilsVarsComponentLlm=inject(UtilsVarsComponentLlm) // Mantido para personalização
  private speechSynthesizerService=inject(SpeechSynthesizerService) // NOVO: Injeta o serviço de síntese de voz
  private _playMessageService=inject(PlayMessageService)
  private _console = inject(Tools_aiConsoleService);

  /**
   * Lista de mensagens base que podem ser personalizadas. (Mensagens de boas-vindas)
   */
  private baseWelcomeMessages: string[] = [
    "_saludo_, senhor. Em que posso auxiliá-l_sexo_ hoje?", // Formal
    "Olá _nome_, estou à disposição para ajudá-l_sexo_. Por favor, informe-me qual tarefa deseja realizar.", // Formal
    "_saludo_. Conte comigo para facilitar sua rotina; como posso ser útil neste momento?", // Formal
    "Saudações. Por gentileza, indique a tarefa em que posso auxiliá-l_sexo_.", // Formal
    "Olá _nome_, tudo bem? Diga-me como posso ajudar com suas atividades.", // Neutro/Respeitoso
    "_saludo_, estou aqui para colaborar com o que precisar. Qual é a sua solicitação?", // Neutro/Respeitoso
    "Olá _nome_, como posso contribuir para tornar seu dia mais produtivo?", // Neutro
    "Oi, estou pronto para ajudar. Por favor, me informe o que deseja fazer.", // Neutro
    "_saludo_, estou preparado para executar sua solicitação. Conte comigo!", // Neutro
    "Oi, diga aí: em que posso facilitar seu trabalho hoje?", // Neutro/Conversacional
    "E aí, tudo certo? Pode contar comigo para o que precisar.", // Informal
    "Oi, estou aqui para dar uma força. Qual tarefa você quer realizar agora?", // Informal
    "Olá _nome_, pode me dizer como posso ajudar a resolver isso?", // Informal
    "Oi, sou seu assistente digital. Fique à vontade para me dizer o que você precisa.", // Informal
    "_saludo_, estou na área para agilizar suas tarefas. Me fala o que você deseja fazer.", // Informal
    "Oi, conta comigo! Qual serviço você gostaria de acessar agora?", // Informal
    "E aí, beleza? Me diz como posso ajudar e vamos resolver isso juntos.", // Informal
    "Oi, estou aqui para facilitar sua vida. Fala aí, qual é a sua demanda?", // Informal
    "_saludo_, estou pronto para ajudar no que for necessário. Qual é a sua solicitação?", // Informal
    "Oi, estou aqui para tornar seu dia mais prático. Me diga, no que posso colaborar?", // Informal
    "Seja bem-vind_sexo_! Em que posso servi-l_sexo_ hoje?", // Formal
    "_saludo_, estou à disposição para atender sua solicitação. Por gentileza, informe a ação desejada.", // Formal
    "Olá _nome_, é um prazer auxiliá-l_sexo_. Por favor, indique a tarefa que deseja realizar.", // Formal
    `Prezad_sexo_ _nome_, sinta-se à vontade para informar qual serviço precisa neste momento.`, // Formal
    "Saudações, estou aqui para colaborar com suas atividades de forma eficiente. Qual o seu pedido?", // Formal
    "Olá _nome_, estou preparado para ajudar. Por favor, me diga como posso contribuir agora.", // Neutro/Respeitoso
    "_saludo_, pronto para executar sua solicitação. Qual serviço deseja acessar?", // Neutro/Respeitoso
    "Olá _nome_, informe qual tarefa precisa de apoio que estou aqui para facilitar sua vida.", // Neutro
    "_saludo_, conte comigo para agilizar seu processo. Qual ação você deseja realizar?", // Neutro
    "Oi, estou à disposição para colaborar com suas demandas. Me fala o que precisa.", // Neutro/Conversacional
    "E aí, tudo certo? Qual desafio vamos superar hoje?", // Informal
    "Oi, beleza? Estou aqui para te dar aquela força. O que deseja fazer?", // Informal
    "Fala, estou na área para ajudar. Manda ver na tarefa que você quer realizar!", // Informal
    "Oi, sou seu assistente! Conta aí qual serviço precisa agora.", // Informal
    "_saludo_! Vamos juntos resolver o que for preciso. Qual é a sua escolha?", // Informal
    "Oi, estou aqui para descomplicar as coisas. Diz aí o que precisa!", // Informal
    "E aí, qual a boa? Vamos agilizar esse serviço juntos.", // Informal
    "Oi, tudo bem? Fala qual a tarefa que eu te ajudo rapidinho.", // Informal
    "Fala aí, parceir_sexo_! Estou aqui para ajudar. Qual é a demanda do momento?", // Informal
    "Oi, meu chapa! Me diz o que você precisa e vamos resolver de uma vez." // Informal
  ];

  /**
   * Lista de mensagens base de despedida que podem ser personalizadas.
   */
  private baseFarewellMessages: string[] = [
    "Agradecemos seu contato, _nome_. A Tecinco Tecnologia permanece à sua inteira disposição para futuras necessidades. Tenha um excelent_sexo_ _saludo_.", // Formal
    "Foi um prazer auxiliá-l_sexo_, _nome_. Conte sempre com o apoio da Tecinco Tecnologia para suas demandas. Desejamos-lh_sexo_ um bom _saludo_.", // Formal
    "_saludo_, senhor(a) _nome_. A Tecinco Tecnologia agradece a preferência e está pronta para novas colaborações. Até breve.", // Formal
    "Esperamos ter atendido às suas expectativas, _nome_. A Tecinco Tecnologia se dedica a oferecer as melhores soluções. Um _saludo_!", // Formal
    "_saludo_, _nome_. A equipe da Tecinco Tecnologia está sempre empenhada em sua satisfação. Retorne quando precisar.", // Formal
    "Certo, _nome_! Se precisar de mais alguma coisa, a Tecinco Tecnologia está por aqui. Tenha um ótimo _saludo_.", // Neutro
    "_saludo_, _nome_. Esperamos ter ajudado! A Tecinco Tecnologia agradece seu contato e fica à disposição.", // Neutro
    "Missão cumprida, _nome_! A Tecinco Tecnologia está sempre buscando facilitar sua vida. Até a próxima.", // Neutro
    "Obrigado por sua confiança, _nome_. A Tecinco Tecnologia segue trabalhando para oferecer o melhor suporte. Bom _saludo_!", // Neutro
    "_saludo_, _nome_. Foi bom ajudar! Lembre-se que a Tecinco Tecnologia é sua parceira em tecnologia.", // Neutro
    "Falou, _nome_! Qualquer coisa, é só chamar a Tecinco Tecnologia. Forte abraç_sexo_ e bom _saludo_!", // Informal
    "E aí, _nome_! Resolvido! A Tecinco Tecnologia tá aqui pra isso. Até mais!", // Informal
    "Valeu, _nome_! A Tecinco Tecnologia sempre dando uma força. Tamo junto!", // Informal
    "Fui, _nome_! Se precisar de um help, a Tecinco Tecnologia te ajuda. Bom _saludo_!", // Informal
    "_saludo_, _nome_! Conte com a Tecinco Tecnologia pra descomplicar. Abraç_sexo_!" // Informal
  ];


  private welcomeMessages: string[] = []; // Mensagens de boas-vindas personalizadas
  private farewellMessages: string[] = []; // Mensagens de despedida personalizadas

  /**
   * Arreglo auxiliar que contém os índices dos mensagens de boas-vindas que ainda não foram utilizados.
   * Usado para garantir que a seleção de um mensagem aleatório não se repita até que
   * a lista completa tenha sido consumida.
   */
  private welcomeIndicesDisponibles: number[] = [];

  /**
   * Arreglo auxiliar que contém os índices dos mensagens de despedida que ainda não foram utilizados.
   * Usado para garantir que a seleção de um mensagem aleatório não se repita até que
   * a lista completa tenha sido consumida.
   */
  private farewellIndicesDisponibles: number[] = [];


  /**
   * Constructor do serviço.
   * Inicializa a lista de índices disponíveis e as mensagens personalizadas.
   */
  constructor(){this._initializeMessages();}

  /**
   * Inicializa e personaliza as mensagens de forma assíncrona.
   * Este método é chamado uma vez após a inicialização do serviço.
   */
  private async _initializeMessages(): Promise<void> {
    try {
      const _saludo_ = await this._utilsVarsComponentLlm.getSaludoPorHora();
      const userData = await this._utilsVarsComponentLlm.getUserDataRelevant();
      const senhor_var = userData.sexo === 'female' ? 'senhora' : 'senhor';
      const sexo_var = userData.sexo === 'female' ? 'a' : 'o';
      const userName = userData.name || '';

      // Personaliza mensagens de boas-vindas
      this.welcomeMessages = this.baseWelcomeMessages.map(mensaje => {
        let personalizedMessage = mensaje.replace('_saludo_', _saludo_);
        personalizedMessage = personalizedMessage.replace('senhor', senhor_var);
        personalizedMessage = personalizedMessage.replaceAll('_sexo_', sexo_var);
        personalizedMessage = personalizedMessage.replace('_nome_', userName);
        return personalizedMessage;
      });
      this.reiniciarIndicesBoasVindas();

      // Personaliza mensagens de despedida
      this.farewellMessages = this.baseFarewellMessages.map(mensaje => {
        let personalizedMessage = mensaje.replace('_saludo_', _saludo_);
        personalizedMessage = personalizedMessage.replace('senhor', senhor_var); // Embora não usado, mantido por consistência
        personalizedMessage = personalizedMessage.replaceAll('_sexo_', sexo_var);
        personalizedMessage = personalizedMessage.replace('_nome_', userName);
        return personalizedMessage;
      });
      this.reiniciarIndicesDespedida();

      this._console.log('MessageService: Mensagens personalizadas carregadas e índices reiniciados.');
    } catch (error) {
      this._console.error("MessageService: Erro ao inicializar mensagens:", error);
      this.welcomeMessages = [...this.baseWelcomeMessages]; // Fallback para mensagens base
      this.reiniciarIndicesBoasVindas();
      this.farewellMessages = [...this.baseFarewellMessages]; // Fallback para mensagens base de despedida
      this.reiniciarIndicesDespedida();
    }
  }

  /**
   * Reinicia o array de índices disponíveis para mensagens de boas-vindas.
   */
  private reiniciarIndicesBoasVindas(): void {
    if (this.welcomeMessages.length === 0) {
      this._console.warn("MessageService: Mensagens de boas-vindas ainda não foram carregadas, não é possível reiniciar índices.");
      return;
    }
    this.welcomeIndicesDisponibles = this.welcomeMessages.map((_, index) => index);
  }

  /**
   * Reinicia o array de índices disponíveis para mensagens de despedida.
   */
  private reiniciarIndicesDespedida(): void {
    if (this.farewellMessages.length === 0) {
      this._console.warn("MessageService: Mensagens de despedida ainda não foram carregadas, não é possível reiniciar índices.");
      return;
    }
    this.farewellIndicesDisponibles = this.farewellMessages.map((_, index) => index);
  }

  /**
   * Retorna um mensagem aleatório da lista de boas-vindas sem repetição durante o ciclo atual.
   * O método seleciona aleatoriamente uma posição no array `welcomeIndicesDisponibles`,
   * obtém o mensagem correspondente e remove o índice selecionado para evitar repetição.
   * Se já não houverem índices disponíveis (todos os mensagens foram usados), o método
   * reinicia o pool de índices.
   *
   * @returns {Promise<void>} Uma Promise que resolve quando a fala do mensagem termina.
   */
  public async obterMensagemAleatoria(): Promise<void> {
    this._console.log('MessageService: Obtenção de mensagem de boas-vindas aleatória.');
    if (this.welcomeIndicesDisponibles.length === 0) {
      this.reiniciarIndicesBoasVindas();
    }
    const posAleatoria = Math.floor(Math.random() * this.welcomeIndicesDisponibles.length);
    const indiceMensaje = this.welcomeIndicesDisponibles[posAleatoria];
    this.welcomeIndicesDisponibles.splice(posAleatoria, 1);
    const messageToSpeak = this.welcomeMessages[indiceMensaje];
    await this._playMessageService.playMessage(messageToSpeak);
    return
  }

  /**
   * Retorna um mensagem aleatório da lista de despedida sem repetição durante o ciclo atual.
   * O método seleciona aleatoriamente uma posição no array `farewellIndicesDisponibles`,
   * obtém o mensagem correspondente e remove o índice selecionado para evitar repetição.
   * Se já não houverem índices disponíveis (todos os mensagens foram usados), o método
   * reinicia o pool de índices.
   *
   * @returns {Promise<void>} Uma Promise que resolve quando a fala do mensagem termina.
   */
  public async obterMensagemDespedidaAleatoria(): Promise<void> {
    this._console.log('MessageService: Obtenção de mensagem de despedida aleatória.');
    if (this.farewellIndicesDisponibles.length === 0) {
      this.reiniciarIndicesDespedida();
    }
    const posAleatoria = Math.floor(Math.random() * this.farewellIndicesDisponibles.length);
    const indiceMensaje = this.farewellIndicesDisponibles[posAleatoria];
    this.farewellIndicesDisponibles.splice(posAleatoria, 1);
    const messageToSpeak = this.farewellMessages[indiceMensaje];
    await this._playMessageService.playMessage(messageToSpeak);
    return;
  }

  /**
   * Método auxiliar para usar o `SpeechSynthesizerService` diretamente se necessário,
   * por exemplo, para mensagens que não fazem parte da rotação de mensagens aleatórias.
   * @param text Texto a falar.
   * @param settings Configurações de fala.
   */
  public async speakCustomMessage(text: string, settings?: SpeechSettings): Promise<void> {
    return this.speechSynthesizerService.speak(text, settings);
  }

  /**
   * Pausa a síntese de voz através do serviço dedicado.
   */
  public pauseSpeech(): void {
    this.speechSynthesizerService.pause();
  }

  /**
   * Retoma a síntese de voz através do serviço dedicado.
   */
  public resumeSpeech(): void {
    this.speechSynthesizerService.resume();
  }

  /**
   * Cancela a síntese de voz através do serviço dedicado.
   */
  public cancelSpeech(): void {
    this.speechSynthesizerService.cancel();
  }
}
